import React, { useState, useEffect, useCallback } from 'react';
import { AD_TEMPLATES, AD_TEMPLATE_CATEGORIES } from '../data/fbAdTemplates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Build a deep-link that opens this exact campaign/ad inside Meta Ads Manager.
// accountId may be like "act_4301678703408054"; Ads Manager wants the numeric part.
function metaAdsManagerUrl(accountId, { campaignId, adsetId, adId } = {}) {
  const num = String(accountId || '').replace(/^act_/, '').trim();
  if (!num) return null;
  const params = new URLSearchParams({ act: num });
  if (campaignId) params.set('selected_campaign_ids', campaignId);
  if (adsetId) params.set('selected_adset_ids', adsetId);
  if (adId) params.set('selected_ad_ids', adId);
  // Show at the deepest level we have an id for.
  const level = (adId || adsetId) ? 'ads' : 'campaigns';
  return `https://adsmanager.facebook.com/adsmanager/manage/${level}?${params.toString()}`;
}


// Resilient JSON reader. The Emergent platform monitoring script (emergent-main.js)
// wraps window.fetch and reads the body of error responses, which can leave the
// original Response body consumed ("body stream already read"). We grab our own
// clone the instant fetch resolves and fall back through several read strategies
// so the component never crashes and we surface the real backend error message.
async function readJson(res) {
  let clone = null;
  try { clone = res.clone(); } catch { clone = null; }
  if (clone) {
    try { return await clone.json(); } catch { /* fall through */ }
  }
  try { return await res.json(); } catch { /* fall through */ }
  try {
    const txt = await res.text();
    return txt ? JSON.parse(txt) : null;
  } catch { return null; }
}

// Extracts a human-readable, bilingual-friendly message from a backend/Meta error.
function extractErrorMsg(data, fallback) {
  if (!data) return fallback;
  const detail = data.detail || data.error || data.message;
  if (!detail) return fallback;
  if (typeof detail === 'string') {
    // Surface Meta's localized "error_user_msg" / "error_user_title" if present.
    const m = detail.match(/"error_user_msg":\s*"((?:[^"\\]|\\.)*)"/);
    if (m && m[1]) { try { return JSON.parse('"' + m[1] + '"'); } catch { return m[1]; } }
    const t = detail.match(/"error_user_title":\s*"((?:[^"\\]|\\.)*)"/);
    if (t && t[1]) { try { return JSON.parse('"' + t[1] + '"'); } catch { return t[1]; } }
    return detail;
  }
  if (Array.isArray(detail)) return detail.map(d => d?.msg || JSON.stringify(d)).join(', ');
  if (typeof detail === 'object') return detail.error_user_msg || detail.message || JSON.stringify(detail);
  return fallback;
}

// Uploads a local image file to our storage and returns an ABSOLUTE public URL
// (Meta downloads it server-side, so a relative path won't work).
async function uploadAdImage(file, token) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${API_URL}/api/image-upload/feature-image`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: fd,
  });
  const data = await readJson(res);
  if (!data || !data.success || !data.url) {
    throw new Error(extractErrorMsg(data, 'Image upload failed'));
  }
  return data.url.startsWith('http') ? data.url : `${API_URL}${data.url}`;
}

const OBJECTIVES = [
  { value: 'OUTCOME_AWARENESS', label: 'Brand Awareness', icon: 'fa-eye', desc: 'ज़्यादा लोगों तक पहुँचें' },
  { value: 'OUTCOME_TRAFFIC', label: 'Traffic', icon: 'fa-arrow-pointer', desc: 'Website पर visitors लाएं' },
  { value: 'OUTCOME_LEADS', label: 'Lead Generation', icon: 'fa-user-plus', desc: 'Leads collect करें' },
  { value: 'OUTCOME_ENGAGEMENT', label: 'Engagement / WhatsApp 💬', icon: 'fa-comments', desc: 'WhatsApp chats + Likes, comments' },
  { value: 'OUTCOME_SALES', label: 'Sales', icon: 'fa-cart-shopping', desc: 'Sales और conversions' },
];

const OPTIMIZATION_GOALS = [
  { value: 'CONVERSATIONS', label: '💬 WhatsApp Chats (सुझाव)', desc: 'Click-to-WhatsApp — chat शुरू कराएँ' },
  { value: 'LEAD_GENERATION', label: '🎯 Leads (Instant Form)', desc: 'Instant Form से leads लाएँ (App Review ज़रूरी)' },
  { value: 'LINK_CLICKS', label: 'Link Clicks', desc: 'Website clicks बढ़ाएं' },
  { value: 'LANDING_PAGE_VIEWS', label: 'Landing Page Views', desc: 'Page views बढ़ाएं' },
  // NOTE: REACH & IMPRESSIONS hटा दिए गए — ये सिर्फ़ खर्च करते हैं, leads नहीं लाते।
  // Backend भी इन्हें block करता है (VANITY_OPTIMIZATION_GOALS) ताकि गलती से न बनें।
];

const CTA_OPTIONS = [
  { value: 'LEARN_MORE', label: 'Learn More' },
  { value: 'SIGN_UP', label: 'Sign Up' },
  { value: 'CONTACT_US', label: 'Contact Us' },
  { value: 'BOOK_TRAVEL', label: 'Book Now' },
  { value: 'GET_QUOTE', label: 'Get Quote' },
  { value: 'APPLY_NOW', label: 'Apply Now' },
  { value: 'DOWNLOAD', label: 'Download' },
  { value: 'SEND_WHATSAPP_MESSAGE', label: 'WhatsApp Message' },
];

const INDIAN_STATES = [
  'Madhya Pradesh', 'Uttar Pradesh', 'Maharashtra', 'Rajasthan', 'Gujarat',
  'Bihar', 'Karnataka', 'Tamil Nadu', 'Telangana', 'Andhra Pradesh',
  'West Bengal', 'Punjab', 'Haryana', 'Delhi', 'Chhattisgarh',
];

// Meta (Facebook) region keys for Indian states — fetched live from the
// Graph API adgeolocation search. Meta requires these exact numeric keys;
// a made-up slug is rejected. Source: /v25.0/search?type=adgeolocation&country_code=IN
const STATE_REGION_KEYS = {
  'Madhya Pradesh': '1753', 'Uttar Pradesh': '1754', 'Maharashtra': '1735',
  'Rajasthan': '1743', 'Gujarat': '1729', 'Bihar': '1752', 'Karnataka': '1738',
  'Tamil Nadu': '1744', 'Telangana': '4100', 'Andhra Pradesh': '1724',
  'West Bengal': '1746', 'Punjab': '1742', 'Haryana': '1730', 'Delhi': '1728',
  'Chhattisgarh': '1755',
};

const STATUS_COLORS = {
  ACTIVE: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  PAUSED: 'bg-amber-100 text-amber-700 border-amber-300',
  DELETED: 'bg-red-100 text-red-700 border-red-300',
  ARCHIVED: 'bg-gray-100 text-gray-600 border-gray-300',
};

function StatusBadge({ status }) {
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-500'}`} data-testid={`status-badge-${status}`}>
      {status === 'ACTIVE' && <i className="fa-solid fa-circle text-[6px] mr-1 animate-pulse"></i>}
      {status}
    </span>
  );
}

// Human-readable (Hindi) hint for Meta's review/effective status
const EFFECTIVE_STATUS_INFO = {
  ACTIVE: { label: '✅ Live चल रहा है', cls: 'text-emerald-600' },
  PENDING_REVIEW: { label: '⏳ Meta review में (मंज़ूरी का इंतज़ार)', cls: 'text-blue-600' },
  IN_PROCESS: { label: '⏳ Meta review में', cls: 'text-blue-600' },
  PREAPPROVED: { label: '⏳ Pre-approved (जल्द live)', cls: 'text-blue-600' },
  DISAPPROVED: { label: '❌ Meta ने reject किया — Ad edit करें', cls: 'text-red-600' },
  WITH_ISSUES: { label: '⚠️ Issue है — Ads Manager में देखें', cls: 'text-red-600' },
  PENDING_BILLING_INFO: { label: '💳 Payment method जोड़ें', cls: 'text-red-600' },
  CAMPAIGN_PAUSED: { label: '⏸️ Campaign paused है', cls: 'text-amber-600' },
  ADSET_PAUSED: { label: '⏸️ Ad Set paused है', cls: 'text-amber-600' },
  PAUSED: { label: '⏸️ Paused — Live करें', cls: 'text-amber-600' },
  ARCHIVED: { label: 'Archived', cls: 'text-gray-500' },
  DELETED: { label: 'Deleted', cls: 'text-gray-500' },
};

function EffectiveStatusHint({ status }) {
  if (!status) return null;
  const info = EFFECTIVE_STATUS_INFO[status] || { label: status, cls: 'text-gray-500' };
  return <span className={`text-[10px] font-medium ${info.cls}`} data-testid={`eff-status-${status}`}>{info.label}</span>;
}


// ==================== AD SET CREATE MODAL ====================
function AdSetCreateModal({ campaignId, campaignName, token, onClose, onCreated }) {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    campaign_id: campaignId,
    name: '',
    daily_budget: 50000,
    optimization_goal: 'CONVERSATIONS',
    billing_event: 'IMPRESSIONS',
    age_min: 25,
    age_max: 60,
    genders: [1, 2],
    geo_locations: { countries: ['IN'] },
    interests: [],
    status: 'PAUSED',
  });
  const [interestInput, setInterestInput] = useState('');
  const [interestResults, setInterestResults] = useState([]);
  const [interestSearching, setInterestSearching] = useState(false);

  // Live Meta interest search — only real interest IDs are valid (subcode 1487079).
  useEffect(() => {
    const q = interestInput.trim();
    if (q.length < 2) { setInterestResults([]); return; }
    let cancelled = false;
    setInterestSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/api/campaign/facebook-ads/interest-search?q=${encodeURIComponent(q)}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const data = await readJson(res);
        if (!cancelled) setInterestResults((data && data.interests) || []);
      } catch {
        if (!cancelled) setInterestResults([]);
      }
      if (!cancelled) setInterestSearching(false);
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [interestInput, token]);

  const addInterest = (it) => {
    if (!it || !it.id) return;
    setForm(f => (f.interests.some(x => x.id === it.id)
      ? f
      : { ...f, interests: [...f.interests, { id: it.id, name: it.name }] }));
    setInterestInput('');
    setInterestResults([]);
  };

  const removeInterest = (idx) => {
    setForm(f => ({ ...f, interests: f.interests.filter((_, i) => i !== idx) }));
  };

  const toggleGender = (g) => {
    setForm(f => {
      const has = f.genders.includes(g);
      return { ...f, genders: has ? f.genders.filter(x => x !== g) : [...f.genders, g] };
    });
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return;
    setCreating(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/campaign/facebook-ads/create-adset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await readJson(res);
      if (data && data.success) {
        onCreated(data);
        onClose();
      } else {
        setError(extractErrorMsg(data, `Ad Set create करने में error (HTTP ${res.status})`));
      }
    } catch (err) {
      setError('Ad Set create failed: ' + err.message);
    }
    setCreating(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" data-testid="adset-create-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl mx-4 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center justify-between shrink-0">
          <div className="text-white">
            <h3 className="text-lg font-bold flex items-center gap-2"><i className="fa-solid fa-bullseye"></i> New Ad Set</h3>
            <p className="text-sm text-white/70">Campaign: {campaignName}</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition" data-testid="close-adset-modal">
            <i className="fa-solid fa-xmark text-xl"></i>
          </button>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
              <i className="fa-solid fa-circle-exclamation mr-2"></i>{error}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Ad Set Name *</label>
            <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-indigo-500 outline-none transition"
              placeholder="e.g., Principals 30-55 MP" data-testid="adset-name-input" />
          </div>

          {/* Budget & Optimization */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Daily Budget (INR)</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">₹</span>
                <input type="number" value={form.daily_budget / 100}
                  onChange={e => setForm({ ...form, daily_budget: Math.max(1, parseInt(e.target.value) || 0) * 100 })}
                  className="w-full pl-8 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-indigo-500 outline-none transition"
                  min="1" data-testid="adset-budget-input" />
              </div>
              <p className="text-[11px] text-gray-400 mt-1">रोज़ का खर्च (Auto bidding — bid amount की ज़रूरत नहीं)</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Optimization Goal</label>
              <select value={form.optimization_goal} onChange={e => setForm({ ...form, optimization_goal: e.target.value })}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-indigo-500 outline-none transition" data-testid="adset-optimization-select">
                {OPTIMIZATION_GOALS.map(g => <option key={g.value} value={g.value}>{g.label} — {g.desc}</option>)}
              </select>
              {form.optimization_goal === 'CONVERSATIONS' && (
                <p className="text-[11px] text-emerald-600 mt-1">💬 WhatsApp ad — Campaign objective <b>Engagement</b> रखें। Lead सीधे bot पर आएगी।</p>
              )}
            </div>
          </div>

          {/* Targeting Section */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-4">
            <h4 className="font-semibold text-gray-700 flex items-center gap-2">
              <i className="fa-solid fa-crosshairs text-indigo-500"></i> Targeting
            </h4>

            {/* Age */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Age Min</label>
                <input type="number" value={form.age_min} onChange={e => setForm({ ...form, age_min: parseInt(e.target.value) || 18 })}
                  className="w-full px-3 py-2 border rounded-lg text-sm" min={18} max={65} data-testid="adset-age-min" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Age Max</label>
                <input type="number" value={form.age_max} onChange={e => setForm({ ...form, age_max: parseInt(e.target.value) || 65 })}
                  className="w-full px-3 py-2 border rounded-lg text-sm" min={18} max={65} data-testid="adset-age-max" />
              </div>
            </div>

            {/* Gender */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Gender</label>
              <div className="flex gap-2">
                {[{ val: 1, label: 'Male', icon: 'fa-mars' }, { val: 2, label: 'Female', icon: 'fa-venus' }].map(g => (
                  <button key={g.val} onClick={() => toggleGender(g.val)}
                    className={`flex-1 py-2 rounded-lg border-2 text-sm font-medium transition flex items-center justify-center gap-2 ${form.genders.includes(g.val) ? 'border-indigo-400 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-400'}`}
                    data-testid={`adset-gender-${g.val}`}>
                    <i className={`fa-solid ${g.icon}`}></i> {g.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Location (India — States)</label>
              <div className="flex flex-wrap gap-1.5">
                {INDIAN_STATES.map(state => (
                  <button key={state} onClick={() => {
                    setForm(f => {
                      const regions = f.geo_locations.regions || [];
                      const has = regions.some(r => r.name === state);
                      const nextRegions = has
                        ? regions.filter(r => r.name !== state)
                        : [...regions, { key: STATE_REGION_KEYS[state], name: state }];
                      return {
                        ...f,
                        // When specific states are chosen, target only those regions.
                        // When none are left, fall back to All-India (country IN).
                        geo_locations: nextRegions.length === 0
                          ? { countries: ['IN'] }
                          : { regions: nextRegions },
                      };
                    });
                  }}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition ${(form.geo_locations.regions || []).some(r => r.name === state) ? 'bg-indigo-100 text-indigo-700 border-indigo-300' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'}`}
                    data-testid={`adset-state-${state.replace(/\s/g, '-')}`}>
                    {state}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {(form.geo_locations.regions || []).length === 0 ? 'All India selected (default)' : `${(form.geo_locations.regions || []).length} states selected`}
              </p>
            </div>

            {/* Interests */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Interests (Optional)</label>
              <div className="relative">
                <input type="text" value={interestInput} onChange={e => setInterestInput(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Type to search — e.g., Education, Parenting" data-testid="adset-interest-input" autoComplete="off" />
                {interestSearching && (
                  <i className="fa-solid fa-spinner animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                )}
                {interestInput.trim().length >= 2 && interestResults.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto" data-testid="adset-interest-results">
                    {interestResults.map(it => (
                      <button key={it.id} onClick={() => addInterest(it)} type="button"
                        className="w-full text-left px-3 py-2 hover:bg-indigo-50 text-sm border-b border-gray-50 last:border-0"
                        data-testid={`adset-interest-option-${it.id}`}>
                        <span className="font-medium text-gray-700">{it.name}</span>
                        {it.path && <span className="block text-[10px] text-gray-400">{it.path}</span>}
                        {it.audience_size ? <span className="block text-[10px] text-indigo-400">~{Number(it.audience_size).toLocaleString('en-IN')} audience</span> : null}
                      </button>
                    ))}
                  </div>
                )}
                {interestInput.trim().length >= 2 && !interestSearching && interestResults.length === 0 && (
                  <p className="text-[11px] text-gray-400 mt-1">कोई matching interest नहीं मिला — दूसरा शब्द आज़माएँ</p>
                )}
              </div>
              <p className="text-[11px] text-gray-400 mt-1">सिर्फ़ list में से चुनें (Meta के valid interests)। खाली छोड़ने पर सबको target करेगा।</p>
              {form.interests.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {form.interests.map((int_, i) => (
                    <span key={i} className="px-2.5 py-1 bg-indigo-50 text-indigo-700 text-xs rounded-full flex items-center gap-1 border border-indigo-200">
                      {int_.name}
                      <button onClick={() => removeInterest(i)} className="hover:text-red-500 ml-0.5"><i className="fa-solid fa-xmark text-[10px]"></i></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Status */}
          <div className="flex gap-3">
            {['PAUSED', 'ACTIVE'].map(st => (
              <button key={st} onClick={() => setForm({ ...form, status: st })}
                className={`flex-1 py-2.5 rounded-xl border-2 font-medium text-sm transition ${form.status === st ? (st === 'ACTIVE' ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-amber-400 bg-amber-50 text-amber-700') : 'border-gray-200 text-gray-500'}`}
                data-testid={`adset-status-${st}`}>
                <i className={`fa-solid ${st === 'ACTIVE' ? 'fa-play' : 'fa-pause'} mr-2`}></i>
                {st === 'ACTIVE' ? 'Active' : 'Paused'}
              </button>
            ))}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 py-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 font-medium text-gray-600">Cancel</button>
            <button onClick={handleSubmit} disabled={!form.name.trim() || creating}
              className="flex-1 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="submit-adset-btn">
              {creating ? <><i className="fa-solid fa-spinner animate-spin"></i> Creating...</> : <><i className="fa-solid fa-bullseye"></i> Create Ad Set</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ==================== AD CREATIVE CREATE MODAL ====================
function AdCreateModal({ adsetId, adsetName, token, onClose, onCreated }) {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    adset_id: adsetId,
    name: '',
    headline: '',
    body: '',
    link_url: 'https://shiksha-mitra.com',
    image_url: '',
    call_to_action: 'SEND_WHATSAPP_MESSAGE',
    lead_gen_form_id: '',
    destination: 'whatsapp', // 'whatsapp' | 'website' | 'lead_form'
    whatsapp_prefill: 'नमस्ते 🙏 मुझे शिक्षा मित्र के बारे में जानना है — Free Demo चाहिए।',
    status: 'PAUSED',
  });
  const [leadForms, setLeadForms] = useState([]);
  const [creatingForm, setCreatingForm] = useState(false);

  const loadLeadForms = async () => {
    try {
      const res = await fetch(`${API_URL}/api/campaign/facebook-ads/lead-forms`, {
        headers: { 'Authorization': `Bearer ${token}` } });
      const data = await readJson(res);
      if (data && data.success) setLeadForms(data.forms || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadLeadForms(); /* eslint-disable-next-line */ }, []);

  const createLeadForm = async () => {
    const name = window.prompt('नए Lead Form का नाम (जैसे: Admission Enquiry):', 'Shiksha Mitra — Admission Enquiry');
    if (!name) return;
    setCreatingForm(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/campaign/facebook-ads/lead-forms`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, intro_headline: 'अपने स्कूल के लिए फ्री डेमो बुक करें', intro_description: 'नाम व फ़ोन भरें — हमारी टीम आपसे संपर्क करेगी।' }) });
      const data = await readJson(res);
      if (data && data.success) {
        await loadLeadForms();
        setForm(f => ({ ...f, lead_gen_form_id: data.form_id, call_to_action: f.call_to_action === 'LEARN_MORE' ? 'SIGN_UP' : f.call_to_action }));
      } else setError(extractErrorMsg(data, 'Form create failed'));
    } catch (e) { setError('Form create failed: ' + e.message); }
    setCreatingForm(false);
  };
  const [realBody, setRealBody] = useState('');
  const [realLoading, setRealLoading] = useState(false);
  const [realErr, setRealErr] = useState('');
  const [realFormat, setRealFormat] = useState('MOBILE_FEED_STANDARD');
  const [showTpl, setShowTpl] = useState(true);
  const [tplCat, setTplCat] = useState('modules');

  const applyTemplate = (tpl) => {
    const img = `${API_URL}/api/image-upload/serve/shiksha-mitra/ad-templates/${tpl.id}.png`;
    setForm(f => ({ ...f, name: tpl.name, headline: tpl.headline, body: tpl.body, call_to_action: tpl.cta || 'LEARN_MORE', image_url: img }));
    setRealBody(''); setRealErr('');
  };

  const fetchRealPreview = async (fmt) => {
    const useFmt = fmt || realFormat;
    setRealFormat(useFmt); setRealLoading(true); setRealErr(''); setRealBody('');
    try {
      const res = await fetch(`${API_URL}/api/campaign/facebook-ads/generate-preview`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          headline: form.headline, body: form.body, link_url: form.link_url,
          call_to_action: form.call_to_action, image_url: form.image_url, ad_format: useFmt,
        }),
      });
      const data = await res.json();
      if (data.success && data.body) setRealBody(data.body);
      else setRealErr(data.detail || 'Preview लोड नहीं हो पाया');
    } catch (e) { setRealErr('Preview लोड नहीं हो पाया'); }
    setRealLoading(false);
  };

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const url = await uploadAdImage(file, token);
      setForm(f => ({ ...f, image_url: url }));
    } catch (err) {
      setError('Image upload failed: ' + err.message);
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.headline.trim() || !form.body.trim()) return;
    setCreating(true);
    setError('');
    // Build payload based on the chosen destination
    const payload = { ...form };
    if (form.destination === 'whatsapp') {
      payload.call_to_action = 'SEND_WHATSAPP_MESSAGE';
      payload.lead_gen_form_id = '';
      // whatsapp_number left empty → backend uses connected FB_WHATSAPP_NUMBER
    } else if (form.destination === 'website') {
      payload.lead_gen_form_id = '';
      payload.whatsapp_number = '';
      if (payload.call_to_action === 'SEND_WHATSAPP_MESSAGE') payload.call_to_action = 'LEARN_MORE';
    } else if (form.destination === 'lead_form') {
      payload.whatsapp_number = '';
    }
    try {
      const res = await fetch(`${API_URL}/api/campaign/facebook-ads/create-ad`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await readJson(res);
      if (data && data.success) {
        onCreated(data);
        onClose();
      } else {
        setError(extractErrorMsg(data, `Ad create करने में error (HTTP ${res.status})`));
      }
    } catch (err) {
      setError('Ad create failed: ' + err.message);
    }
    setCreating(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" data-testid="ad-create-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl mx-4 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="bg-gradient-to-r from-pink-600 to-rose-600 px-6 py-4 flex items-center justify-between shrink-0">
          <div className="text-white">
            <h3 className="text-lg font-bold flex items-center gap-2"><i className="fa-solid fa-palette"></i> New Ad Creative</h3>
            <p className="text-sm text-white/70">Ad Set: {adsetName}</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition" data-testid="close-ad-modal">
            <i className="fa-solid fa-xmark text-xl"></i>
          </button>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto">
          {/* Destination — कहाँ ले जाए? WhatsApp (recommended) / Website / Lead Form */}
          <div className="border-2 border-emerald-200 rounded-xl p-3 bg-emerald-50/50" data-testid="ad-destination-section">
            <label className="block text-sm font-semibold text-gray-700 mb-2">📍 Ad पर click करने पर user कहाँ जाए?</label>
            <div className="grid grid-cols-3 gap-2 mb-2">
              <button type="button" onClick={() => setForm({ ...form, destination: 'whatsapp', call_to_action: 'SEND_WHATSAPP_MESSAGE' })}
                data-testid="dest-whatsapp-btn"
                className={`px-2 py-2 rounded-lg text-xs font-semibold border-2 transition ${form.destination === 'whatsapp' ? 'bg-[#25D366] text-white border-[#25D366]' : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-300'}`}>
                💬 WhatsApp
              </button>
              <button type="button" onClick={() => setForm({ ...form, destination: 'website', call_to_action: form.call_to_action === 'SEND_WHATSAPP_MESSAGE' ? 'LEARN_MORE' : form.call_to_action, lead_gen_form_id: '' })}
                data-testid="dest-website-btn"
                className={`px-2 py-2 rounded-lg text-xs font-semibold border-2 transition ${form.destination === 'website' ? 'bg-rose-600 text-white border-rose-600' : 'bg-white text-gray-600 border-gray-200 hover:border-rose-300'}`}>
                🌐 Website
              </button>
              <button type="button" onClick={() => setForm({ ...form, destination: 'lead_form' })}
                data-testid="dest-leadform-btn"
                className={`px-2 py-2 rounded-lg text-xs font-semibold border-2 transition ${form.destination === 'lead_form' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'}`}>
                📋 Lead Form
              </button>
            </div>

            {form.destination === 'whatsapp' && (
              <div className="bg-white rounded-lg p-2.5 border border-emerald-200" data-testid="dest-whatsapp-info">
                <p className="text-xs text-emerald-800 font-medium">✅ Click-to-WhatsApp Ad — user "WhatsApp भेजें" दबाते ही सीधे आपके Aayush bot (<b>+91 91831 00206</b>) पर chat खोलेगा।</p>
                <p className="text-[11px] text-gray-500 mt-1">कोई Lead Form / Meta App Review की ज़रूरत नहीं — leads तुरंत bot + CRM में आएँगी। 🚀</p>
                <label className="block text-[11px] font-semibold text-gray-600 mt-2 mb-1">💬 Pre-filled message (user का पहला message)</label>
                <textarea value={form.whatsapp_prefill} rows={2} onChange={e => setForm({ ...form, whatsapp_prefill: e.target.value })}
                  className="w-full px-2.5 py-2 border-2 border-gray-200 rounded-lg focus:border-emerald-500 outline-none resize-none text-xs" data-testid="ad-whatsapp-prefill-input" />
                <p className="text-[10px] text-gray-400 mt-1">WhatsApp खुलते ही यह message पहले से टाइप मिलेगा — user सिर्फ़ Send दबाएगा।</p>
                <div className="mt-2 pt-2 border-t border-emerald-100 bg-amber-50 -mx-2.5 -mb-2.5 px-2.5 py-2 rounded-b-lg">
                  <p className="text-[10px] text-amber-800 leading-relaxed">
                    ⚠️ अगर Meta लाल warning दिखाए <b>"Your Page is not linked to a WhatsApp account"</b> — तो आपका Facebook Page WhatsApp नंबर से जुड़ा नहीं है। एक बार link कीजिए:
                  </p>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    <a href="https://business.facebook.com/settings/whatsapp-business-accounts" target="_blank" rel="noopener noreferrer"
                      data-testid="link-page-whatsapp-business" className="text-[10px] px-2 py-1 rounded-md bg-emerald-600 text-white font-medium hover:bg-emerald-700">🔗 WhatsApp Accounts → Page जोड़ें</a>
                    <a href="https://business.facebook.com/latest/settings/whatsapp" target="_blank" rel="noopener noreferrer"
                      data-testid="link-page-whatsapp-suite" className="text-[10px] px-2 py-1 rounded-md border border-emerald-300 text-emerald-700 font-medium hover:bg-emerald-50">🔗 Business Suite → WhatsApp connect</a>
                  </div>
                </div>
              </div>
            )}

            {form.destination === 'lead_form' && (
              <div data-testid="ad-leadform-section">
                <p className="text-[11px] text-gray-500 mb-2">⚠️ Lead Form के लिए Meta का <b>leads_retrieval</b> permission (App Review) ज़रूरी है। आसान रास्ता — ऊपर 💬 WhatsApp चुनें।</p>
                <div className="flex gap-2">
                  <select value={form.lead_gen_form_id} onChange={e => setForm({ ...form, lead_gen_form_id: e.target.value, call_to_action: e.target.value && form.call_to_action === 'LEARN_MORE' ? 'SIGN_UP' : form.call_to_action })}
                    className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg text-sm bg-white" data-testid="ad-leadform-select">
                    <option value="">— Form चुनें —</option>
                    {leadForms.map(f => <option key={f.id} value={f.id}>{f.name} (leads: {f.leads_count})</option>)}
                  </select>
                  <button type="button" onClick={createLeadForm} disabled={creatingForm} data-testid="ad-create-leadform-btn"
                    className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 whitespace-nowrap">
                    {creatingForm ? <i className="fa-solid fa-spinner animate-spin"></i> : '+ नया Form'}
                  </button>
                </div>
                {form.lead_gen_form_id && <p className="text-xs text-emerald-700 mt-2" data-testid="ad-leadform-active">✅ Lead Form लगा है</p>}
              </div>
            )}
          </div>

          {/* Ready-made templates */}
          <div className="bg-indigo-50/60 rounded-xl p-4 border border-indigo-100" data-testid="ad-templates-section">
            <button type="button" onClick={() => setShowTpl(s => !s)} className="w-full flex items-center justify-between" data-testid="toggle-templates">
              <span className="font-bold text-indigo-700 text-sm">📋 Ready-made Ad Templates ({AD_TEMPLATES.length})</span>
              <i className={`fa-solid fa-chevron-${showTpl ? 'up' : 'down'} text-indigo-400 text-xs`}></i>
            </button>
            {showTpl && (
              <div className="mt-3">
                <p className="text-xs text-gray-500 mb-2">एक template चुनें — form अपने-आप भर जाएगा (फिर edit/preview कर सकते हैं)।</p>
                <div className="flex gap-1.5 flex-wrap mb-3">
                  {AD_TEMPLATE_CATEGORIES.map(c => (
                    <button type="button" key={c.key} onClick={() => setTplCat(c.key)}
                      data-testid={`tpl-cat-${c.key}`}
                      className={`text-[11px] px-2.5 py-1 rounded-full font-medium ${tplCat === c.key ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 border'}`}>
                      {c.label}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-52 overflow-y-auto pr-1">
                  {AD_TEMPLATES.filter(t => t.cat === tplCat).map(t => (
                    <button type="button" key={t.id} onClick={() => applyTemplate(t)}
                      data-testid={`tpl-${t.id}`}
                      className="text-left bg-white border rounded-lg overflow-hidden hover:border-indigo-400 hover:shadow-sm transition flex">
                      <img src={`${API_URL}/api/image-upload/serve/shiksha-mitra/ad-templates/${t.id}.png`}
                        alt={t.title} loading="lazy"
                        className="w-16 h-16 object-cover shrink-0 bg-gray-100"
                        onError={(e) => { e.target.style.display = 'none'; }} />
                      <div className="p-2 min-w-0">
                        <p className="text-xs font-semibold text-gray-800 truncate">{t.title}</p>
                        <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{t.headline}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
              <i className="fa-solid fa-circle-exclamation mr-2"></i>{error}
            </div>
          )}

          {/* Ad Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Ad Name *</label>
            <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-rose-500 outline-none transition"
              placeholder="e.g., School ERP Ad - Hindi" data-testid="ad-name-input" />
          </div>

          {/* Headline & Body */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Headline * <span className="text-xs text-gray-400 font-normal">(40 chars max)</span></label>
              <input type="text" value={form.headline} onChange={e => setForm({ ...form, headline: e.target.value })}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-rose-500 outline-none transition"
                placeholder="e.g., AI से अपने स्कूल को Smart बनाएं" maxLength={40} data-testid="ad-headline-input" />
              <p className="text-xs text-gray-400 text-right mt-0.5">{form.headline.length}/40</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Ad Body / Primary Text * <span className="text-xs text-gray-400 font-normal">(125 chars recommended)</span></label>
              <textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-rose-500 outline-none transition resize-none"
                rows={3} placeholder="e.g., 31 AI Features, Attendance, Exams, Fees - सब एक जगह। Free Demo Book करें!" data-testid="ad-body-input" />
              <p className="text-xs text-gray-400 text-right mt-0.5">{form.body.length} chars</p>
            </div>
          </div>

          {/* Link URL — only relevant for a website-destination ad */}
          {form.destination === 'website' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Landing Page URL</label>
              <input type="url" value={form.link_url} onChange={e => setForm({ ...form, link_url: e.target.value })}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-rose-500 outline-none transition"
                placeholder="https://shiksha-mitra.com" data-testid="ad-link-input" />
            </div>
          )}

          {/* Image: upload OR URL */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Ad Image <span className="text-xs text-gray-400 font-normal">(1200x628 recommended)</span>
            </label>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium cursor-pointer transition ${uploading ? 'bg-gray-100 text-gray-400' : 'bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-200'}`} data-testid="ad-image-upload-label">
                {uploading ? <i className="fa-solid fa-spinner animate-spin"></i> : <i className="fa-solid fa-upload"></i>}
                {uploading ? 'Uploading...' : 'Computer/Phone से Upload करें'}
                <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} data-testid="ad-image-file-input" />
              </label>
              {form.image_url && (
                <button type="button" onClick={() => setForm({ ...form, image_url: '' })}
                  className="text-xs text-gray-500 hover:text-red-500" data-testid="ad-image-clear-btn">
                  <i className="fa-solid fa-xmark mr-1"></i>हटाएं
                </button>
              )}
            </div>
            <input type="url" value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-rose-500 outline-none transition text-sm"
              placeholder="या image URL paste करें — https://example.com/ad.jpg" data-testid="ad-image-input" />
            {form.image_url && (
              <div className="mt-2 rounded-xl overflow-hidden border max-h-40">
                <img src={form.image_url} alt="Preview" className="w-full h-40 object-cover"
                  onError={e => { e.target.style.display = 'none'; }} />
              </div>
            )}
          </div>

          {/* CTA & Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Call to Action</label>
              <select value={form.call_to_action} onChange={e => setForm({ ...form, call_to_action: e.target.value })}
                disabled={form.destination === 'whatsapp'}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-rose-500 outline-none transition disabled:bg-gray-100 disabled:text-gray-500" data-testid="ad-cta-select">
                {CTA_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              {form.destination === 'whatsapp' && <p className="text-[11px] text-emerald-600 mt-1">WhatsApp ad के लिए CTA fix है</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Status</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-rose-500 outline-none transition" data-testid="ad-status-select">
                <option value="PAUSED">Paused (बाद में शुरू)</option>
                <option value="ACTIVE">Active (तुरंत शुरू)</option>
              </select>
            </div>
          </div>

          {/* Live Preview */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-3 font-semibold">Ad Preview</p>
            <div className="bg-white rounded-lg shadow border overflow-hidden max-w-sm mx-auto">
              <div className="p-2.5 flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-bold text-xs">SM</div>
                <div>
                  <p className="font-semibold text-xs text-gray-900">Shiksha Mitra</p>
                  <p className="text-[10px] text-gray-500">Sponsored</p>
                </div>
              </div>
              <div className="px-2.5 pb-2">
                <p className="text-xs text-gray-800 leading-relaxed">{form.body || 'Ad body text...'}</p>
              </div>
              {form.image_url ? (
                <img src={form.image_url} alt="Ad" className="w-full h-32 object-cover" onError={e => { e.target.style.display = 'none'; }} />
              ) : (
                <div className="bg-gradient-to-br from-orange-400 to-amber-500 h-32 flex items-center justify-center">
                  <p className="text-white font-bold text-sm text-center px-4">{form.headline || 'Headline'}</p>
                </div>
              )}
              <div className="px-2.5 py-2 bg-gray-50 border-t flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-gray-500 uppercase">shiksha-mitra.com</p>
                  <p className="text-xs font-semibold text-gray-800">{form.headline || 'Headline'}</p>
                </div>
                <span className="px-3 py-1 bg-[#1877F2] text-white text-[10px] font-medium rounded">
                  {CTA_OPTIONS.find(c => c.value === form.call_to_action)?.label || 'Learn More'}
                </span>
              </div>
            </div>
          </div>

          {/* Real Facebook preview (before publishing) */}
          <div className="bg-white rounded-xl p-4 border">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <p className="text-xs text-gray-500">Facebook पर असल में कैसा दिखेगा — बनाने से पहले देखें</p>
              <button type="button" onClick={() => fetchRealPreview()} disabled={realLoading}
                className="text-xs px-3 py-1.5 rounded-lg bg-[#1877F2] text-white font-medium hover:bg-blue-700 disabled:opacity-50"
                data-testid="generate-fb-preview-btn">
                {realLoading ? 'लोड हो रहा...' : '👁️ Facebook पर देखें'}
              </button>
            </div>
            {(realBody || realErr) && (
              <>
                <div className="flex gap-2 flex-wrap mb-3">
                  {PREVIEW_FORMATS.map(f => (
                    <button type="button" key={f.value} onClick={() => fetchRealPreview(f.value)}
                      className={`text-xs px-2.5 py-1 rounded-full ${realFormat === f.value ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                      {f.label}
                    </button>
                  ))}
                </div>
                {realErr ? (
                  <p className="text-xs text-red-500 text-center py-3" data-testid="generate-fb-preview-error">{realErr}</p>
                ) : (
                  <div className="flex justify-center overflow-x-auto" data-testid="generate-fb-preview-frame"
                    dangerouslySetInnerHTML={{ __html: realBody }} />
                )}
              </>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 py-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 font-medium text-gray-600">Cancel</button>
            <button onClick={handleSubmit} disabled={!form.name.trim() || !form.headline.trim() || !form.body.trim() || creating}
              className="flex-1 py-3 bg-rose-600 text-white rounded-xl hover:bg-rose-700 font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="submit-ad-btn">
              {creating ? <><i className="fa-solid fa-spinner animate-spin"></i> Creating...</> : <><i className="fa-solid fa-palette"></i> Create Ad</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ==================== AD IMAGE UPDATE MODAL (existing/live ads) ====================
function AdImageUpdateModal({ ad, token, onClose, onUpdated }) {
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true); setError('');
    try {
      const url = await uploadAdImage(file, token);
      setImageUrl(url);
    } catch (err) {
      setError('Image upload failed: ' + err.message);
    }
    setUploading(false);
    e.target.value = '';
  };

  const save = async () => {
    if (!imageUrl.trim()) { setError('पहले नई image upload करें या URL डालें'); return; }
    setSaving(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/campaign/facebook-ads/ad/${ad.id}/update-image`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_url: imageUrl }),
      });
      const data = await readJson(res);
      if (data && data.success) {
        setDone(true);
        onUpdated && onUpdated();
        setTimeout(onClose, 1200);
      } else {
        setError(extractErrorMsg(data, `Image update failed (HTTP ${res.status})`));
      }
    } catch (err) {
      setError('Image update failed: ' + err.message);
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" data-testid="ad-image-update-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg mx-4 shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-rose-600 to-orange-500 px-6 py-4 flex items-center justify-between">
          <div className="text-white">
            <h3 className="text-lg font-bold flex items-center gap-2"><i className="fa-solid fa-image"></i> Ad की Image बदलें</h3>
            <p className="text-sm text-white/80 truncate">{ad.name}</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="close-image-update-modal">
            <i className="fa-solid fa-xmark text-xl"></i>
          </button>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700" data-testid="image-update-error">
              <i className="fa-solid fa-circle-exclamation mr-2"></i>{error}
            </div>
          )}
          {done ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-6 text-center text-emerald-700" data-testid="image-update-success">
              <i className="fa-solid fa-circle-check text-2xl mb-2"></i>
              <p className="font-semibold">Image बदल गई! ✅</p>
              <p className="text-xs mt-1">Ad का text/link वही रहेगा, सिर्फ़ image नई होगी।</p>
            </div>
          ) : (
            <>
              <p className="text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <i className="fa-solid fa-circle-info text-amber-500 mr-1"></i>
                नई image के साथ ad का बाकी content (headline, text, link) वैसा ही रहेगा।
              </p>
              <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium cursor-pointer transition ${uploading ? 'bg-gray-100 text-gray-400' : 'bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-200'}`} data-testid="update-image-upload-label">
                {uploading ? <i className="fa-solid fa-spinner animate-spin"></i> : <i className="fa-solid fa-upload"></i>}
                {uploading ? 'Uploading...' : 'Computer/Phone से नई image चुनें'}
                <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} data-testid="update-image-file-input" />
              </label>
              <input type="url" value={imageUrl} onChange={e => setImageUrl(e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-rose-500 outline-none transition text-sm"
                placeholder="या image URL paste करें" data-testid="update-image-url-input" />
              {imageUrl && (
                <div className="rounded-xl overflow-hidden border max-h-44">
                  <img src={imageUrl} alt="New" className="w-full h-44 object-cover" onError={e => { e.target.style.display = 'none'; }} />
                </div>
              )}
              <div className="flex gap-3 pt-1">
                <button onClick={onClose} className="flex-1 py-2.5 border-2 border-gray-200 rounded-xl hover:bg-gray-50 font-medium text-gray-600">Cancel</button>
                <button onClick={save} disabled={!imageUrl.trim() || saving || uploading}
                  className="flex-1 py-2.5 bg-rose-600 text-white rounded-xl hover:bg-rose-700 font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="save-image-update-btn">
                  {saving ? <><i className="fa-solid fa-spinner animate-spin"></i> Saving...</> : <><i className="fa-solid fa-check"></i> Image बदलें</>}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ==================== MAIN COMPONENT ====================

// ==================== CAMPAIGN EDIT MODAL ====================
function CampaignEditModal({ campaign, token, onClose, onSaved }) {
  const [name, setName] = useState(campaign.name || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    if (!name.trim()) { setError('Name ज़रूरी है'); return; }
    setSaving(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/campaign/facebook-ads/campaign/${campaign.id}`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() }),
      });
      const data = await readJson(res);
      if (data && data.success) { onSaved(); onClose(); }
      else setError(extractErrorMsg(data, `Save failed (HTTP ${res.status})`));
    } catch (e) { setError('Save failed: ' + e.message); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" data-testid="campaign-edit-modal">
      <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><i className="fa-solid fa-pen"></i> Campaign Edit करें</h3>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="close-campaign-edit"><i className="fa-solid fa-xmark text-xl"></i></button>
        </div>
        <div className="p-6 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700" data-testid="campaign-edit-error">{error}</div>}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Campaign Name</label>
            <input value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 outline-none" data-testid="campaign-edit-name" />
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 py-2.5 border-2 border-gray-200 rounded-xl hover:bg-gray-50 font-medium text-gray-600">Cancel</button>
            <button onClick={save} disabled={saving} className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium disabled:opacity-50 flex items-center justify-center gap-2" data-testid="save-campaign-edit-btn">
              {saving ? <><i className="fa-solid fa-spinner animate-spin"></i> Saving...</> : <><i className="fa-solid fa-check"></i> Save</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== AD SET EDIT MODAL ====================
function AdSetEditModal({ adset, token, onClose, onSaved }) {
  const t = adset.targeting || {};
  const [name, setName] = useState(adset.name || '');
  const [budget, setBudget] = useState(adset.daily_budget || 50000); // paisa
  const [ageMin, setAgeMin] = useState(t.age_min || 25);
  const [ageMax, setAgeMax] = useState(t.age_max || 60);
  const [genders, setGenders] = useState(t.genders || [1, 2]);
  const [regions, setRegions] = useState((t.geo_locations && t.geo_locations.regions) || []);
  const [interests, setInterests] = useState((t.flexible_spec && t.flexible_spec[0] && t.flexible_spec[0].interests) || []);
  const [interestInput, setInterestInput] = useState('');
  const [interestResults, setInterestResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Placements: detect current mode from targeting (publisher_platforms set + no reels = feed_only)
  const fbPos = t.facebook_positions || [];
  const initialPlacement = (t.publisher_platforms && !fbPos.includes('facebook_reels')) ? 'feed_only' : 'automatic';
  const [placementMode, setPlacementMode] = useState(initialPlacement);
  const [placementBusy, setPlacementBusy] = useState(false);
  const [placementMsg, setPlacementMsg] = useState('');

  const applyPlacement = async (mode) => {
    setPlacementBusy(true); setPlacementMsg(''); setError('');
    try {
      const res = await fetch(`${API_URL}/api/campaign/facebook-ads/adset/${adset.id}/placements?mode=${mode}`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}` } });
      const data = await readJson(res);
      if (data && data.success) {
        setPlacementMode(mode);
        setPlacementMsg(mode === 'feed_only' ? '✅ अब ad सिर्फ़ Feed पर दिखेगा (Reels बंद)' : '✅ Automatic placements चालू (सभी जगह)');
      } else setError(extractErrorMsg(data, 'Placement update failed'));
    } catch (e) { setError('Placement error: ' + e.message); }
    setPlacementBusy(false);
  };

  useEffect(() => {
    const q = interestInput.trim();
    if (q.length < 2) { setInterestResults([]); return; }
    let cancelled = false;
    const tm = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/api/campaign/facebook-ads/interest-search?q=${encodeURIComponent(q)}`, { headers: { 'Authorization': `Bearer ${token}` } });
        const data = await readJson(res);
        if (!cancelled) setInterestResults((data && data.interests) || []);
      } catch { if (!cancelled) setInterestResults([]); }
    }, 350);
    return () => { cancelled = true; clearTimeout(tm); };
  }, [interestInput, token]);

  const toggleGender = (g) => setGenders(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]);
  const toggleState = (st) => setRegions(prev => prev.some(r => r.name === st)
    ? prev.filter(r => r.name !== st)
    : [...prev, { key: STATE_REGION_KEYS[st], name: st }]);

  const save = async () => {
    if (!name.trim()) { setError('Name ज़रूरी है'); return; }
    if (genders.length === 0) { setError('कम से कम एक gender चुनें'); return; }
    setSaving(true); setError('');
    const geo = regions.length > 0 ? { regions } : { countries: ['IN'] };
    try {
      const res = await fetch(`${API_URL}/api/campaign/facebook-ads/adset/${adset.id}`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(), daily_budget: budget, age_min: ageMin, age_max: ageMax,
          genders, geo_locations: geo, interests: interests.map(i => ({ id: i.id, name: i.name })),
        }),
      });
      const data = await readJson(res);
      if (data && data.success) {
        if (placementMode === 'feed_only') {
          try {
            await fetch(`${API_URL}/api/campaign/facebook-ads/adset/${adset.id}/placements?mode=feed_only`, {
              method: 'PUT', headers: { 'Authorization': `Bearer ${token}` } });
          } catch { /* non-fatal */ }
        }
        onSaved(); onClose();
      }
      else setError(extractErrorMsg(data, `Save failed (HTTP ${res.status})`));
    } catch (e) { setError('Save failed: ' + e.message); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="adset-edit-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center justify-between shrink-0">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><i className="fa-solid fa-pen"></i> Ad Set Edit करें</h3>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="close-adset-edit"><i className="fa-solid fa-xmark text-xl"></i></button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">
          {error && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700" data-testid="adset-edit-error">{error}</div>}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Ad Set Name</label>
            <input value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-indigo-500 outline-none" data-testid="adset-edit-name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Daily Budget (₹)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">₹</span>
                <input type="number" value={budget / 100} min="1" onChange={e => setBudget(Math.max(1, parseInt(e.target.value) || 0) * 100)} className="w-full pl-7 pr-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-indigo-500 outline-none" data-testid="adset-edit-budget" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Age min</label>
                <input type="number" value={ageMin} min="13" max="65" onChange={e => setAgeMin(parseInt(e.target.value) || 13)} className="w-full px-2 py-2.5 border-2 border-gray-200 rounded-xl text-sm" data-testid="adset-edit-agemin" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Age max</label>
                <input type="number" value={ageMax} min="13" max="65" onChange={e => setAgeMax(parseInt(e.target.value) || 65)} className="w-full px-2 py-2.5 border-2 border-gray-200 rounded-xl text-sm" data-testid="adset-edit-agemax" />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Gender</label>
            <div className="flex gap-2">
              {[{ v: 1, l: 'Male' }, { v: 2, l: 'Female' }].map(g => (
                <button key={g.v} onClick={() => toggleGender(g.v)} className={`px-4 py-2 rounded-lg text-sm border-2 ${genders.includes(g.v) ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-500'}`} data-testid={`adset-edit-gender-${g.v}`}>{g.l}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">States (कुछ न चुनें = पूरा India)</label>
            <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
              {Object.keys(STATE_REGION_KEYS).map(st => (
                <button key={st} onClick={() => toggleState(st)} className={`px-2.5 py-1 rounded-full text-xs border ${regions.some(r => r.name === st) ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-500'}`} data-testid={`adset-edit-state-${st.replace(/\s/g, '-')}`}>{st}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Interests (optional)</label>
            <div className="relative">
              <input value={interestInput} onChange={e => setInterestInput(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Type to search — e.g., Education" autoComplete="off" data-testid="adset-edit-interest-input" />
              {interestInput.trim().length >= 2 && interestResults.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-44 overflow-y-auto">
                  {interestResults.map(it => (
                    <button key={it.id} type="button" onClick={() => { if (!interests.some(x => x.id === it.id)) setInterests([...interests, { id: it.id, name: it.name }]); setInterestInput(''); setInterestResults([]); }} className="w-full text-left px-3 py-2 hover:bg-indigo-50 text-sm border-b last:border-0">{it.name}</button>
                  ))}
                </div>
              )}
            </div>
            {interests.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {interests.map((int_, i) => (
                  <span key={i} className="px-2.5 py-1 bg-indigo-50 text-indigo-700 text-xs rounded-full flex items-center gap-1 border border-indigo-200">{int_.name}<button onClick={() => setInterests(interests.filter((_, x) => x !== i))} className="hover:text-red-500"><i className="fa-solid fa-xmark text-[10px]"></i></button></span>
                ))}
              </div>
            )}
          </div>
          <div className="border-2 border-rose-100 rounded-xl p-3 bg-rose-50/40" data-testid="adset-placements-section">
            <label className="block text-sm font-semibold text-gray-700 mb-1">📍 Placements — ad कहाँ दिखे</label>
            <p className="text-[11px] text-gray-500 mb-2">Image ad के लिए "सिर्फ़ Feed" best है — इससे Reels recommendation भी हट जाती है।</p>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => applyPlacement('automatic')} disabled={placementBusy}
                className={`px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition disabled:opacity-50 ${placementMode === 'automatic' ? 'border-indigo-400 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                data-testid="placement-automatic-btn">
                🤖 Automatic<br /><span className="text-[10px] font-normal">(Reels समेत सभी जगह)</span>
              </button>
              <button type="button" onClick={() => applyPlacement('feed_only')} disabled={placementBusy}
                className={`px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition disabled:opacity-50 ${placementMode === 'feed_only' ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                data-testid="placement-feedonly-btn">
                🎯 सिर्फ़ Feed<br /><span className="text-[10px] font-normal">(Reels/Stories बंद)</span>
              </button>
            </div>
            {placementBusy && <p className="text-xs text-gray-500 mt-2"><i className="fa-solid fa-spinner animate-spin mr-1"></i>बदला जा रहा है...</p>}
            {placementMsg && <p className="text-xs text-emerald-700 mt-2" data-testid="placement-msg">{placementMsg}</p>}
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 py-2.5 border-2 border-gray-200 rounded-xl hover:bg-gray-50 font-medium text-gray-600">Cancel</button>
            <button onClick={save} disabled={saving} className="flex-1 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 font-medium disabled:opacity-50 flex items-center justify-center gap-2" data-testid="save-adset-edit-btn">
              {saving ? <><i className="fa-solid fa-spinner animate-spin"></i> Saving...</> : <><i className="fa-solid fa-check"></i> Save</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== AD EDIT MODAL (text/link/cta) ====================
function AdEditModal({ ad, token, onClose, onSaved }) {
  const [name, setName] = useState(ad.name || '');
  const [headline, setHeadline] = useState(ad.headline || ad.creative_headline || '');
  const [body, setBody] = useState(ad.body || ad.creative_body || '');
  const [linkUrl, setLinkUrl] = useState(ad.link_url || 'https://shiksha-mitra.com');
  const [cta, setCta] = useState(ad.call_to_action || 'LEARN_MORE');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    if (!name.trim()) { setError('Name ज़रूरी है'); return; }
    setSaving(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/campaign/facebook-ads/ad/${ad.id}`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), headline, body, link_url: linkUrl, call_to_action: cta }),
      });
      const data = await readJson(res);
      if (data && data.success) { onSaved(); onClose(); }
      else setError(extractErrorMsg(data, `Save failed (HTTP ${res.status})`));
    } catch (e) { setError('Save failed: ' + e.message); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="ad-edit-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="bg-gradient-to-r from-rose-600 to-orange-500 px-6 py-4 flex items-center justify-between shrink-0">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><i className="fa-solid fa-pen"></i> Ad Edit करें</h3>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="close-ad-edit"><i className="fa-solid fa-xmark text-xl"></i></button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">
          {error && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700" data-testid="ad-edit-error">{error}</div>}
          <p className="text-[11px] text-gray-500 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"><i className="fa-solid fa-circle-info text-amber-500 mr-1"></i> Image बदलने के लिए "Image बदलें" button इस्तेमाल करें। यहाँ text/link बदलें।</p>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Ad Name</label>
            <input value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-rose-500 outline-none" data-testid="ad-edit-name" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Headline</label>
            <input value={headline} onChange={e => setHeadline(e.target.value)} className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-rose-500 outline-none" placeholder="मुख्य लाइन" data-testid="ad-edit-headline" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Body Text</label>
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={3} className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-rose-500 outline-none resize-none" placeholder="ad का description" data-testid="ad-edit-body" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Link URL</label>
              <input value={linkUrl} onChange={e => setLinkUrl(e.target.value)} className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-rose-500 outline-none text-sm" data-testid="ad-edit-link" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Button (CTA)</label>
              <select value={cta} onChange={e => setCta(e.target.value)} className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-rose-500 outline-none text-sm" data-testid="ad-edit-cta">
                {CTA_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 py-2.5 border-2 border-gray-200 rounded-xl hover:bg-gray-50 font-medium text-gray-600">Cancel</button>
            <button onClick={save} disabled={saving} className="flex-1 py-2.5 bg-rose-600 text-white rounded-xl hover:bg-rose-700 font-medium disabled:opacity-50 flex items-center justify-center gap-2" data-testid="save-ad-edit-btn">
              {saving ? <><i className="fa-solid fa-spinner animate-spin"></i> Saving...</> : <><i className="fa-solid fa-check"></i> Save</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== 1-CLICK WHATSAPP LEAD AD ====================
function QuickWhatsAppModal({ token, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: 'WhatsApp Leads',
    headline: 'अपने स्कूल को Smart बनाएं',
    body: '31 AI Features, Fees, Attendance — सब एक जगह। अभी WhatsApp पर Free Demo बुक करें! 🙏',
    image_url: '',
    daily_budget: 30000,
    prefill_message: 'नमस्ते 🙏 मुझे शिक्षा मित्र के बारे में जानना है — Free Demo चाहिए।',
    go_live: false,
  });
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [showTpl, setShowTpl] = useState(true);
  const [tplCat, setTplCat] = useState('modules');

  const applyTemplate = (tpl) => {
    setForm(f => ({
      ...f,
      name: tpl.title || f.name,
      headline: tpl.headline,
      body: tpl.body,
      image_url: `${API_URL}/api/image-upload/serve/shiksha-mitra/ad-templates/${tpl.id}.png`,
    }));
    setShowTpl(false);
  };

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true); setError('');
    try {
      const url = await uploadAdImage(file, token);
      setForm(f => ({ ...f, image_url: url }));
    } catch (err) { setError('Image upload failed: ' + err.message); }
    setUploading(false);
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.headline.trim() || !form.body.trim()) { setError('Name, Headline और Body भरें'); return; }
    if (!form.image_url.trim()) { setError('एक image ज़रूरी है (WhatsApp ad बिना image नहीं बनता)'); return; }
    setCreating(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/campaign/facebook-ads/quick-whatsapp-ad`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await readJson(res);
      if (data && data.success) setResult(data);
      else setError(extractErrorMsg(data, `Setup failed (HTTP ${res.status})`));
    } catch (err) { setError('Setup failed: ' + err.message); }
    setCreating(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" data-testid="quick-whatsapp-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg mx-4 shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="bg-gradient-to-r from-[#25D366] to-[#128C7E] px-6 py-4 flex items-center justify-between shrink-0">
          <div className="text-white">
            <h3 className="text-lg font-bold flex items-center gap-2"><i className="fa-brands fa-whatsapp"></i> WhatsApp Lead Ad — 1-Click</h3>
            <p className="text-xs text-white/80">Campaign + Ad Set + Ad — सब अपने-आप, सही settings के साथ</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="close-quick-whatsapp"><i className="fa-solid fa-xmark text-xl"></i></button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          {result ? (
            <div className="text-center py-2" data-testid="quick-whatsapp-result">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                <i className="fa-solid fa-check text-2xl text-emerald-600"></i>
              </div>
              <h4 className="font-bold text-gray-800 text-lg">✅ WhatsApp Ad तैयार!</h4>
              <p className="text-sm text-gray-500 mt-1">
                {result.went_live ? 'Ad live कर दिया गया है।' : 'Ad PAUSED बना है — Campaigns list में जाकर 🚀 Live करें।'}
              </p>
              {result.effective_status && (
                <p className="text-xs mt-2 inline-block px-3 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                  Meta status: {result.effective_status === 'ACTIVE' ? '✅ Active' : `⏳ ${result.effective_status} (Meta review में हो सकता है)`}
                </p>
              )}
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mt-4 text-left text-xs text-emerald-800">
                💬 अब जो भी ad पर click करके WhatsApp भेजेगा → Aayush bot (<b>+91 91831 00206</b>) जवाब देगा → lead सीधे आपके <b>CRM Pro</b> में आ जाएगी (auto)।
              </div>
              <button onClick={onCreated} className="mt-4 w-full py-2.5 rounded-xl bg-[#25D366] text-white font-semibold hover:bg-[#1ebe5b]" data-testid="quick-whatsapp-done">
                बढ़िया, हो गया
              </button>
            </div>
          ) : (
            <>
              {error && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700"><i className="fa-solid fa-circle-exclamation mr-2"></i>{error}</div>}

              {/* Template prefill */}
              <div className="bg-indigo-50/60 rounded-xl p-3 border border-indigo-100">
                <button type="button" onClick={() => setShowTpl(s => !s)} className="w-full flex items-center justify-between" data-testid="quick-toggle-templates">
                  <span className="font-semibold text-indigo-700 text-sm">📋 Ready-made template से भरें (optional)</span>
                  <i className={`fa-solid fa-chevron-${showTpl ? 'up' : 'down'} text-indigo-400 text-xs`}></i>
                </button>
                {showTpl && (
                  <div className="mt-3">
                    <div className="flex gap-1.5 flex-wrap mb-2">
                      {AD_TEMPLATE_CATEGORIES.map(c => (
                        <button type="button" key={c.key} onClick={() => setTplCat(c.key)}
                          data-testid={`quick-tpl-cat-${c.key}`}
                          className={`text-[11px] px-2 py-1 rounded-full font-medium ${tplCat === c.key ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 border'}`}>{c.label}</button>
                      ))}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-40 overflow-y-auto pr-1">
                      {AD_TEMPLATES.filter(t => t.cat === tplCat).map(t => (
                        <button type="button" key={t.id} onClick={() => applyTemplate(t)} data-testid={`quick-tpl-${t.id}`}
                          className="text-left bg-white border rounded-lg overflow-hidden hover:border-indigo-400 flex">
                          <img src={`${API_URL}/api/image-upload/serve/shiksha-mitra/ad-templates/${t.id}.png`} alt={t.title} loading="lazy"
                            className="w-12 h-12 object-cover shrink-0 bg-gray-100" onError={(e) => { e.target.style.display = 'none'; }} />
                          <div className="p-1.5 min-w-0"><p className="text-[11px] font-semibold text-gray-800 truncate">{t.title}</p></div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Ad का नाम *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#25D366] outline-none" data-testid="quick-name-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Headline * <span className="text-xs text-gray-400">(40 max)</span></label>
                <input value={form.headline} maxLength={40} onChange={e => setForm({ ...form, headline: e.target.value })}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#25D366] outline-none" data-testid="quick-headline-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Ad Text *</label>
                <textarea value={form.body} rows={3} onChange={e => setForm({ ...form, body: e.target.value })}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#25D366] outline-none resize-none" data-testid="quick-body-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Ad Image * <span className="text-xs text-gray-400">(ज़रूरी)</span></label>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <label className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium cursor-pointer ${uploading ? 'bg-gray-100 text-gray-400' : 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'}`} data-testid="quick-image-upload-label">
                    {uploading ? <i className="fa-solid fa-spinner animate-spin"></i> : <i className="fa-solid fa-upload"></i>}
                    {uploading ? 'Uploading...' : 'Upload करें'}
                    <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} data-testid="quick-image-file-input" />
                  </label>
                  {form.image_url && <button type="button" onClick={() => setForm({ ...form, image_url: '' })} className="text-xs text-gray-500 hover:text-red-500">हटाएं</button>}
                </div>
                {form.image_url && <div className="rounded-xl overflow-hidden border max-h-36"><img src={form.image_url} alt="Preview" className="w-full h-36 object-cover" onError={e => { e.target.style.display = 'none'; }} /></div>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">💬 Pre-filled WhatsApp message <span className="text-xs text-gray-400">(user का पहला message)</span></label>
                <textarea value={form.prefill_message} rows={2} onChange={e => setForm({ ...form, prefill_message: e.target.value })}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#25D366] outline-none resize-none text-sm" data-testid="quick-prefill-input" />
                <p className="text-[11px] text-gray-500 mt-1">WhatsApp खुलते ही यह message पहले से टाइप मिलेगा — user को सिर्फ़ Send दबाना है। 🚀</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">रोज़ का Budget (₹)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">₹</span>
                  <input type="number" min="1" value={form.daily_budget / 100}
                    onChange={e => setForm({ ...form, daily_budget: Math.max(1, parseInt(e.target.value) || 0) * 100 })}
                    className="w-full pl-7 pr-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#25D366] outline-none" data-testid="quick-budget-input" />
                </div>
              </div>
              <label className="flex items-center gap-3 bg-gray-50 rounded-xl p-3 cursor-pointer border-2 border-gray-100" data-testid="quick-golive-toggle">
                <input type="checkbox" checked={form.go_live} onChange={e => setForm({ ...form, go_live: e.target.checked })} className="w-5 h-5 accent-[#25D366]" />
                <div>
                  <p className="text-sm font-semibold text-gray-800">अभी Live करें 🚀</p>
                  <p className="text-[11px] text-gray-500">On करने पर ad तुरंत चलेगा (budget खर्च होगा)। Off रखें तो PAUSED बनेगा।</p>
                </div>
              </label>

              <button onClick={handleSubmit} disabled={creating || uploading}
                className="w-full py-3 rounded-xl bg-[#25D366] text-white font-bold hover:bg-[#1ebe5b] disabled:opacity-50 flex items-center justify-center gap-2" data-testid="quick-whatsapp-submit">
                {creating ? <><i className="fa-solid fa-spinner animate-spin"></i> बना रहे हैं...</> : <><i className="fa-brands fa-whatsapp"></i> {form.go_live ? 'बनाएँ और Live करें' : 'WhatsApp Ad बनाएँ'}</>}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


export default function FacebookAdsManager({ token }) {
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [accountAnalytics, setAccountAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showQuickWA, setShowQuickWA] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [updatingItem, setUpdatingItem] = useState(null);
  const [goLiveResult, setGoLiveResult] = useState(null);
  const [error, setError] = useState('');

  // Campaign detail view
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [adsets, setAdsets] = useState([]);
  const [adsetsLoading, setAdsetsLoading] = useState(false);

  // Ad set detail
  const [expandedAdset, setExpandedAdset] = useState(null);
  const [ads, setAds] = useState([]);
  const [adsLoading, setAdsLoading] = useState(false);

  // Campaign insights
  const [insightsCampaign, setInsightsCampaign] = useState(null);
  const [campaignInsights, setCampaignInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);

  // Modals
  const [showAdSetModal, setShowAdSetModal] = useState(null); // { campaignId, campaignName }
  const [showAdModal, setShowAdModal] = useState(null); // { adsetId, adsetName }
  const [showImageUpdate, setShowImageUpdate] = useState(null); // { ad, adsetId }
  const [showEditCampaign, setShowEditCampaign] = useState(null);
  const [showEditAdset, setShowEditAdset] = useState(null); // { adset, campaignId }
  const [showEditAd, setShowEditAd] = useState(null); // { ad, adsetId }
  const [showPreview, setShowPreview] = useState(null); // { ad }

  const [newCampaign, setNewCampaign] = useState({
    name: '', objective: 'OUTCOME_ENGAGEMENT', daily_budget: 50000, status: 'PAUSED',
  });

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [statusRes, campaignsRes, analyticsRes] = await Promise.all([
        fetch(`${API_URL}/api/campaign/facebook-ads/status`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/campaign/facebook-ads/campaigns`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/campaign/facebook-ads/account-analytics`, { headers: authHeaders() }),
      ]);
      const statusData = await statusRes.json();
      setConnectionStatus(statusData);

      if (campaignsRes.ok) {
        const campData = await campaignsRes.json();
        if (campData.success) setCampaigns(campData.campaigns || []);
      }
      if (analyticsRes.ok) {
        const anlData = await analyticsRes.json();
        if (anlData.success && anlData.metrics) setAccountAnalytics(anlData.metrics);
      }
    } catch (err) {
      setError('Facebook Ads data load करने में error आया');
    }
    setLoading(false);
  }, [authHeaders]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const createCampaign = async () => {
    if (!newCampaign.name.trim()) return;
    setCreating(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/campaign/facebook-ads/create-campaign`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(newCampaign),
      });
      const data = await readJson(res);
      if (data && data.success) {
        setShowCreateModal(false);
        setNewCampaign({ name: '', objective: 'OUTCOME_ENGAGEMENT', daily_budget: 50000, status: 'PAUSED' });
        loadAll();
      } else {
        setError(extractErrorMsg(data, `Campaign create करने में error (HTTP ${res.status})`));
      }
    } catch (err) {
      setError('Campaign create failed: ' + err.message);
    }
    setCreating(false);
  };

  const updateStatus = async (campaignId, newStatus) => {
    setUpdatingStatus(campaignId);
    try {
      const res = await fetch(`${API_URL}/api/campaign/facebook-ads/campaign/${campaignId}/status?new_status=${newStatus}`, {
        method: 'PUT', headers: authHeaders(),
      });
      const data = await readJson(res);
      if (data && data.success) loadAll();
      else setError(extractErrorMsg(data, 'Status update failed'));
    } catch (err) {
      setError('Status update error: ' + err.message);
    }
    setUpdatingStatus(null);
  };

  const loadAdSets = async (campaignId) => {
    if (expandedCampaign === campaignId) {
      setExpandedCampaign(null);
      setAdsets([]);
      setExpandedAdset(null);
      setAds([]);
      return;
    }
    setExpandedCampaign(campaignId);
    setExpandedAdset(null);
    setAds([]);
    setAdsetsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/campaign/facebook-ads/campaign/${campaignId}/adsets`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setAdsets(data.adsets || []);
    } catch (err) {
      console.error('Error loading ad sets:', err);
    }
    setAdsetsLoading(false);
  };

  const loadAds = async (adsetId) => {
    if (expandedAdset === adsetId) {
      setExpandedAdset(null);
      setAds([]);
      return;
    }
    setExpandedAdset(adsetId);
    setAdsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/campaign/facebook-ads/adset/${adsetId}/ads`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setAds(data.ads || []);
    } catch (err) {
      console.error('Error loading ads:', err);
    }
    setAdsLoading(false);
  };

  // Silent refetch (no expand/collapse) — used after status changes
  const refreshAdsets = async (campaignId) => {
    try {
      const res = await fetch(`${API_URL}/api/campaign/facebook-ads/campaign/${campaignId}/adsets`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setAdsets(data.adsets || []);
    } catch (err) { console.error('refreshAdsets', err); }
  };
  const refreshAds = async (adsetId) => {
    try {
      const res = await fetch(`${API_URL}/api/campaign/facebook-ads/adset/${adsetId}/ads`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setAds(data.ads || []);
    } catch (err) { console.error('refreshAds', err); }
  };

  const updateAdsetStatus = async (adsetId, campaignId, newStatus) => {
    setUpdatingItem(adsetId);
    try {
      const res = await fetch(`${API_URL}/api/campaign/facebook-ads/adset/${adsetId}/status?new_status=${newStatus}`, {
        method: 'PUT', headers: authHeaders() });
      const data = await readJson(res);
      if (data && data.success) await refreshAdsets(campaignId);
      else setError(extractErrorMsg(data, 'Ad Set status update failed'));
    } catch (err) { setError('Ad Set status error: ' + err.message); }
    setUpdatingItem(null);
  };

  const updateAdStatus = async (adId, adsetId, newStatus) => {
    setUpdatingItem(adId);
    try {
      const res = await fetch(`${API_URL}/api/campaign/facebook-ads/ad/${adId}/status?new_status=${newStatus}`, {
        method: 'PUT', headers: authHeaders() });
      const data = await readJson(res);
      if (data && data.success) await refreshAds(adsetId);
      else setError(extractErrorMsg(data, 'Ad status update failed'));
    } catch (err) { setError('Ad status error: ' + err.message); }
    setUpdatingItem(null);
  };

  const goLiveAd = async (adId, adsetId, campaignId) => {
    const msg = 'यह Ad को Facebook पर LIVE कर देगा (Campaign + Ad Set + Ad तीनों ACTIVE)। Meta की review के बाद चलना शुरू होगा।\n\n'
      + '💡 सुझाव: अगर यह एक IMAGE ad है, तो बेहतर results व बेफ़ालतू warning से बचने के लिए Ad Set edit में "🎯 सिर्फ़ Feed" placement चुन लें (Reels व Audience-Network video placements बंद हो जाएँगे)।\n\n'
      + 'आगे बढ़ें?';
    if (!window.confirm(msg)) return;
    setUpdatingItem(adId);
    setGoLiveResult(null);
    try {
      const res = await fetch(`${API_URL}/api/campaign/facebook-ads/ad/${adId}/go-live`, {
        method: 'POST', headers: authHeaders() });
      const data = await readJson(res);
      if (data && data.success) {
        setGoLiveResult({ adId, effective_status: data.effective_status });
        await refreshAds(adsetId);
        if (campaignId) await refreshAdsets(campaignId);
      } else setError(extractErrorMsg(data, 'Go Live failed'));
    } catch (err) { setError('Go Live error: ' + err.message); }
    setUpdatingItem(null);
  };

  const loadCampaignInsights = async (campaignId) => {
    if (insightsCampaign === campaignId) {
      setInsightsCampaign(null);
      setCampaignInsights(null);
      return;
    }
    setInsightsCampaign(campaignId);
    setInsightsLoading(true);
    setCampaignInsights(null);
    try {
      const res = await fetch(`${API_URL}/api/campaign/facebook-ads/analytics/${campaignId}`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success && data.metrics) setCampaignInsights(data.metrics);
      else setCampaignInsights({ message: data.message || 'अभी कोई analytics data नहीं है' });
    } catch (err) {
      setCampaignInsights({ message: 'Analytics load करने में error' });
    }
    setInsightsLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48" data-testid="fb-ads-loading">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-3 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-gray-500">Facebook Ads data load हो रहा है...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="facebook-ads-manager">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center justify-between" data-testid="fb-error-banner">
          <p className="text-sm text-red-700"><i className="fa-solid fa-circle-exclamation mr-2"></i>{error}</p>
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-600"><i className="fa-solid fa-xmark"></i></button>
        </div>
      )}

      {goLiveResult && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center justify-between" data-testid="go-live-success-banner">
          <p className="text-sm text-emerald-700">
            <i className="fa-solid fa-rocket mr-2"></i>
            Ad Live कर दिया गया! अब इसकी status: <strong>{(EFFECTIVE_STATUS_INFO[goLiveResult.effective_status] || {label: goLiveResult.effective_status}).label}</strong>
            {goLiveResult.effective_status === 'PENDING_REVIEW' && ' — Meta की मंज़ूरी मिलते ही (कुछ मिनट–24 घंटे) ad अपने-आप चलना शुरू हो जाएगा।'}
          </p>
          <button onClick={() => setGoLiveResult(null)} className="text-emerald-400 hover:text-emerald-600"><i className="fa-solid fa-xmark"></i></button>
        </div>
      )}

      {/* Connection Status Card */}
      <div className={`rounded-xl border-2 p-5 ${connectionStatus?.connected ? 'border-emerald-200 bg-gradient-to-r from-emerald-50 to-white' : 'border-red-200 bg-gradient-to-r from-red-50 to-white'}`} data-testid="fb-connection-status">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${connectionStatus?.connected ? 'bg-[#1877F2]' : 'bg-gray-300'}`}>
              <i className="fa-brands fa-facebook-f text-white text-xl"></i>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-gray-800">{connectionStatus?.account_name || 'Facebook Ads'}</h3>
                {connectionStatus?.connected && (
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full border border-emerald-300">
                    <i className="fa-solid fa-circle-check mr-1"></i>LIVE
                  </span>
                )}
              </div>
              {connectionStatus?.connected ? (
                <p className="text-sm text-gray-500 mt-0.5">{connectionStatus.account_id} &middot; {connectionStatus.currency} &middot; {connectionStatus.timezone}</p>
              ) : (
                <p className="text-sm text-red-500 mt-0.5">{connectionStatus?.error || connectionStatus?.message || 'Not connected'}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {connectionStatus?.connected && connectionStatus?.amount_spent && (
              <div className="text-right">
                <p className="text-xs text-gray-400">Total Spend</p>
                <p className="text-lg font-bold text-gray-700">₹{(parseFloat(connectionStatus.amount_spent) / 100).toLocaleString('en-IN')}</p>
              </div>
            )}
            <button onClick={loadAll} className="p-2 hover:bg-gray-100 rounded-lg transition" data-testid="fb-refresh-btn" title="Refresh">
              <i className="fa-solid fa-arrows-rotate text-gray-500"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Account Analytics */}
      {accountAnalytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="fb-account-analytics">
          {[
            { label: 'Impressions', value: (accountAnalytics.impressions || 0).toLocaleString(), icon: 'fa-eye', color: 'from-blue-500 to-blue-600' },
            { label: 'Clicks', value: (accountAnalytics.clicks || 0).toLocaleString(), icon: 'fa-arrow-pointer', color: 'from-indigo-500 to-indigo-600' },
            { label: 'Reach', value: (accountAnalytics.reach || 0).toLocaleString(), icon: 'fa-users', color: 'from-violet-500 to-violet-600' },
            { label: 'Spend', value: `₹${(accountAnalytics.spend || 0).toLocaleString('en-IN')}`, icon: 'fa-indian-rupee-sign', color: 'from-emerald-500 to-emerald-600' },
          ].map((s, i) => (
            <div key={i} className={`bg-gradient-to-br ${s.color} rounded-xl p-4 text-white`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs opacity-80">{s.label}</p>
                <i className={`fa-solid ${s.icon} text-sm opacity-60`}></i>
              </div>
              <p className="text-2xl font-bold">{s.value}</p>
              <p className="text-xs opacity-60 mt-1">Last 30 days</p>
            </div>
          ))}
        </div>
      )}

      {/* Action Bar */}
      {connectionStatus?.connected && (
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-700 flex items-center gap-2">
            <i className="fa-solid fa-rectangle-ad text-[#1877F2]"></i>
            Campaigns ({campaigns.length})
          </h3>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowQuickWA(true)}
              className="px-4 py-2.5 bg-[#25D366] text-white rounded-xl hover:bg-[#1ebe5b] transition flex items-center gap-2 font-medium text-sm shadow-sm"
              data-testid="quick-whatsapp-ad-btn">
              <i className="fa-brands fa-whatsapp text-base"></i> WhatsApp Lead Ad — 1-Click
            </button>
            <button onClick={() => setShowCreateModal(true)}
              className="px-4 py-2.5 bg-[#1877F2] text-white rounded-xl hover:bg-[#1565c0] transition flex items-center gap-2 font-medium text-sm"
              data-testid="create-fb-campaign-btn">
              <i className="fa-solid fa-plus"></i> New Campaign
            </button>
          </div>
        </div>
      )}

      {/* Campaigns List */}
      {connectionStatus?.connected && (
        <div className="space-y-3" data-testid="fb-campaigns-list">
          {campaigns.length === 0 ? (
            <div className="bg-white rounded-xl border-2 border-dashed border-gray-200 p-10 text-center">
              <i className="fa-solid fa-rectangle-ad text-4xl text-gray-300 mb-3"></i>
              <p className="text-gray-500 font-medium">अभी कोई campaign नहीं है</p>
              <p className="text-gray-400 text-sm mt-1">"New Campaign" बटन से पहला campaign बनाएं</p>
            </div>
          ) : (
            campaigns.map((camp) => (
              <div key={camp.id} className={`bg-white rounded-xl border transition ${expandedCampaign === camp.id ? 'border-blue-300 shadow-md' : 'hover:border-blue-200 hover:shadow-sm'}`} data-testid={`fb-campaign-${camp.id}`}>
                {/* Campaign Row */}
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1 min-w-0 cursor-pointer" onClick={() => loadAdSets(camp.id)}>
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${expandedCampaign === camp.id ? 'bg-blue-500 text-white' : 'bg-blue-50'}`}>
                      <i className={`fa-brands fa-facebook ${expandedCampaign === camp.id ? '' : 'text-[#1877F2]'} text-lg`}></i>
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-800 truncate">{camp.name}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                        <span><i className="fa-solid fa-bullseye mr-1"></i>{camp.objective?.replace('OUTCOME_', '')}</span>
                        <span><i className="fa-regular fa-clock mr-1"></i>{camp.created_time ? new Date(camp.created_time).toLocaleDateString('hi-IN') : '-'}</span>
                        {camp.daily_budget && <span><i className="fa-solid fa-indian-rupee-sign mr-1"></i>{(parseInt(camp.daily_budget) / 100).toLocaleString()}/day</span>}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <StatusBadge status={camp.status} />
                    {camp.status !== 'DELETED' && (
                      <div className="flex items-center gap-1">
                        {camp.status === 'PAUSED' && (
                          <button onClick={() => updateStatus(camp.id, 'ACTIVE')} disabled={updatingStatus === camp.id}
                            className="p-2 rounded-lg hover:bg-emerald-50 text-emerald-600 transition disabled:opacity-50" title="Activate" data-testid={`activate-btn-${camp.id}`}>
                            {updatingStatus === camp.id ? <i className="fa-solid fa-spinner animate-spin text-sm"></i> : <i className="fa-solid fa-play text-sm"></i>}
                          </button>
                        )}
                        {camp.status === 'ACTIVE' && (
                          <button onClick={() => updateStatus(camp.id, 'PAUSED')} disabled={updatingStatus === camp.id}
                            className="p-2 rounded-lg hover:bg-amber-50 text-amber-600 transition disabled:opacity-50" title="Pause" data-testid={`pause-btn-${camp.id}`}>
                            {updatingStatus === camp.id ? <i className="fa-solid fa-spinner animate-spin text-sm"></i> : <i className="fa-solid fa-pause text-sm"></i>}
                          </button>
                        )}
                        <button onClick={() => { if (window.confirm('Campaign delete करना है?')) updateStatus(camp.id, 'DELETED'); }}
                          disabled={updatingStatus === camp.id}
                          className="p-2 rounded-lg hover:bg-red-50 text-red-400 transition disabled:opacity-50" title="Delete" data-testid={`delete-btn-${camp.id}`}>
                          <i className="fa-solid fa-trash-can text-sm"></i>
                        </button>
                      </div>
                    )}
                    <button onClick={() => setShowEditCampaign(camp)}
                      className="p-2 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition text-sm" title="Edit" data-testid={`edit-campaign-btn-${camp.id}`}>
                      <i className="fa-solid fa-pen text-sm"></i>
                    </button>
                    <button onClick={() => loadCampaignInsights(camp.id)}
                      className={`p-2 rounded-lg transition text-sm ${insightsCampaign === camp.id ? 'bg-blue-50 text-blue-600' : 'hover:bg-gray-50 text-gray-400'}`}
                      title="Analytics" data-testid={`analytics-btn-${camp.id}`}>
                      <i className="fa-solid fa-chart-column"></i>
                    </button>
                    {connectionStatus?.account_id && (
                      <a href={metaAdsManagerUrl(connectionStatus.account_id, { campaignId: camp.id })}
                        target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                        className="p-2 rounded-lg transition text-sm text-gray-400 hover:bg-[#e7f0fd] hover:text-[#1877F2]"
                        title="Meta Ads Manager में खोलें" data-testid={`meta-link-campaign-${camp.id}`}>
                        <i className="fa-solid fa-arrow-up-right-from-square"></i>
                      </a>
                    )}
                    <button onClick={() => loadAdSets(camp.id)}
                      className={`p-2 rounded-lg transition text-sm ${expandedCampaign === camp.id ? 'bg-indigo-50 text-indigo-600' : 'hover:bg-gray-50 text-gray-400'}`}
                      title="View Ad Sets" data-testid={`expand-btn-${camp.id}`}>
                      <i className={`fa-solid fa-chevron-${expandedCampaign === camp.id ? 'up' : 'down'}`}></i>
                    </button>
                  </div>
                </div>

                {/* Campaign Insights */}
                {insightsCampaign === camp.id && (
                  <div className="border-t bg-blue-50/50 px-4 py-3" data-testid={`insights-${camp.id}`}>
                    {insightsLoading ? (
                      <div className="flex items-center gap-2 text-gray-400 text-sm py-2"><i className="fa-solid fa-spinner animate-spin"></i> Analytics load हो रहा है...</div>
                    ) : campaignInsights?.message ? (
                      <p className="text-sm text-gray-500 py-2"><i className="fa-solid fa-info-circle mr-2"></i>{campaignInsights.message}</p>
                    ) : campaignInsights ? (
                      <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
                        {[
                          { label: 'Impressions', value: campaignInsights.impressions?.toLocaleString() || '0' },
                          { label: 'Clicks', value: campaignInsights.clicks?.toLocaleString() || '0' },
                          { label: 'Reach', value: campaignInsights.reach?.toLocaleString() || '0' },
                          { label: 'Spend', value: `₹${campaignInsights.spend || 0}` },
                          { label: 'Frequency', value: campaignInsights.frequency?.toFixed(2) || '0' },
                          { label: 'CTR', value: `${campaignInsights.ctr?.toFixed(2) || 0}%` },
                          { label: 'CPC', value: `₹${campaignInsights.cpc?.toFixed(2) || 0}` },
                          { label: 'CPM', value: `₹${campaignInsights.cpm?.toFixed(2) || 0}` },
                        ].map((m, i) => (
                          <div key={i} className="text-center">
                            <p className="text-xs text-gray-400">{m.label}</p>
                            <p className="text-sm font-bold text-gray-700">{m.value}</p>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                )}

                {/* Ad Sets Expandable */}
                {expandedCampaign === camp.id && (
                  <div className="border-t bg-gray-50/80" data-testid={`adsets-panel-${camp.id}`}>
                    <div className="px-4 py-3 flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-gray-600 flex items-center gap-2">
                        <i className="fa-solid fa-bullseye text-indigo-500"></i> Ad Sets
                      </h4>
                      <button onClick={() => setShowAdSetModal({ campaignId: camp.id, campaignName: camp.name })}
                        className="px-3 py-1.5 bg-indigo-500 text-white rounded-lg text-xs font-medium hover:bg-indigo-600 transition flex items-center gap-1.5"
                        data-testid={`create-adset-btn-${camp.id}`}>
                        <i className="fa-solid fa-plus"></i> New Ad Set
                      </button>
                    </div>

                    {adsetsLoading ? (
                      <div className="px-4 pb-4 text-sm text-gray-400 flex items-center gap-2"><i className="fa-solid fa-spinner animate-spin"></i> Loading ad sets...</div>
                    ) : adsets.length === 0 ? (
                      <div className="px-4 pb-4 text-center py-6">
                        <i className="fa-solid fa-bullseye text-2xl text-gray-300 mb-2"></i>
                        <p className="text-sm text-gray-400">कोई Ad Set नहीं है। "New Ad Set" से बनाएं।</p>
                      </div>
                    ) : (
                      <div className="px-4 pb-3 space-y-2">
                        {adsets.map(aset => (
                          <div key={aset.id} className={`bg-white rounded-lg border transition ${expandedAdset === aset.id ? 'border-indigo-300 shadow-sm' : 'hover:border-gray-300'}`} data-testid={`adset-${aset.id}`}>
                            <div className="p-3 flex items-center justify-between cursor-pointer" onClick={() => loadAds(aset.id)}>
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                                  <i className="fa-solid fa-bullseye text-indigo-500 text-sm"></i>
                                </div>
                                <div className="min-w-0">
                                  <p className="font-medium text-sm text-gray-700 truncate">{aset.name}</p>
                                  <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5">
                                    <span>{aset.optimization_goal}</span>
                                    {aset.daily_budget && <span>₹{(parseInt(aset.daily_budget) / 100).toLocaleString()}/day</span>}
                                    {aset.targeting?.age_min && <span>{aset.targeting.age_min}-{aset.targeting.age_max} yrs</span>}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {aset.status === 'PAUSED' && (
                                  <button onClick={(e) => { e.stopPropagation(); updateAdsetStatus(aset.id, camp.id, 'ACTIVE'); }} disabled={updatingItem === aset.id}
                                    className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600 transition disabled:opacity-50" title="Ad Set Activate करें" data-testid={`activate-adset-btn-${aset.id}`}>
                                    {updatingItem === aset.id ? <i className="fa-solid fa-spinner animate-spin text-xs"></i> : <i className="fa-solid fa-play text-xs"></i>}
                                  </button>
                                )}
                                {aset.status === 'ACTIVE' && (
                                  <button onClick={(e) => { e.stopPropagation(); updateAdsetStatus(aset.id, camp.id, 'PAUSED'); }} disabled={updatingItem === aset.id}
                                    className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600 transition disabled:opacity-50" title="Ad Set Pause करें" data-testid={`pause-adset-btn-${aset.id}`}>
                                    {updatingItem === aset.id ? <i className="fa-solid fa-spinner animate-spin text-xs"></i> : <i className="fa-solid fa-pause text-xs"></i>}
                                  </button>
                                )}
                                <button onClick={(e) => { e.stopPropagation(); setShowEditAdset({ adset: aset, campaignId: camp.id }); }}
                                  className="p-1.5 rounded-lg hover:bg-indigo-50 text-gray-400 hover:text-indigo-600 transition" title="Edit Ad Set" data-testid={`edit-adset-btn-${aset.id}`}>
                                  <i className="fa-solid fa-pen text-xs"></i>
                                </button>
                                <StatusBadge status={aset.status} />
                                <i className={`fa-solid fa-chevron-${expandedAdset === aset.id ? 'up' : 'down'} text-xs text-gray-400`}></i>
                              </div>
                            </div>

                            {/* Ads within Ad Set */}
                            {expandedAdset === aset.id && (
                              <div className="border-t bg-gray-50/50 px-3 py-2" data-testid={`ads-panel-${aset.id}`}>
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-xs font-semibold text-gray-500 flex items-center gap-1.5">
                                    <i className="fa-solid fa-palette text-rose-500"></i> Ads
                                  </span>
                                  <button onClick={() => setShowAdModal({ adsetId: aset.id, adsetName: aset.name })}
                                    className="px-2.5 py-1 bg-rose-500 text-white rounded-md text-xs font-medium hover:bg-rose-600 transition flex items-center gap-1"
                                    data-testid={`create-ad-btn-${aset.id}`}>
                                    <i className="fa-solid fa-plus text-[10px]"></i> New Ad
                                  </button>
                                </div>
                                {adsLoading ? (
                                  <p className="text-xs text-gray-400 py-2"><i className="fa-solid fa-spinner animate-spin mr-1"></i>Loading...</p>
                                ) : ads.length === 0 ? (
                                  <p className="text-xs text-gray-400 py-3 text-center">कोई Ad नहीं है। "New Ad" से Ad Creative बनाएं।</p>
                                ) : (
                                  <div className="space-y-1.5">
                                    {ads.map(ad => (
                                      <div key={ad.id} className="bg-white rounded-md border p-2.5 flex items-center justify-between" data-testid={`ad-${ad.id}`}>
                                        <div className="flex items-center gap-2.5 min-w-0">
                                          <div className="w-7 h-7 rounded bg-rose-50 flex items-center justify-center shrink-0">
                                            <i className="fa-solid fa-palette text-rose-500 text-xs"></i>
                                          </div>
                                          <div className="min-w-0">
                                            <p className="text-xs font-medium text-gray-700 truncate">{ad.name}</p>
                                            <div className="flex items-center gap-2">
                                              <p className="text-[10px] text-gray-400">{ad.created_time ? new Date(ad.created_time).toLocaleDateString('hi-IN') : ''}</p>
                                              <EffectiveStatusHint status={ad.effective_status} />
                                            </div>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                          {ad.status === 'PAUSED' ? (
                                            <button onClick={(e) => { e.stopPropagation(); goLiveAd(ad.id, aset.id, camp.id); }} disabled={updatingItem === ad.id}
                                              title="Image ad है? पहले Ad Set edit में 'सिर्फ़ Feed' placement चुनें (Reels/video placements बंद)"
                                              className="px-2.5 py-1 text-[10px] font-bold text-white bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 rounded-md flex items-center gap-1 transition disabled:opacity-50 shadow-sm"
                                              data-testid={`go-live-btn-${ad.id}`}>
                                              {updatingItem === ad.id ? <i className="fa-solid fa-spinner animate-spin"></i> : <i className="fa-solid fa-rocket"></i>} Live करें
                                            </button>
                                          ) : (
                                            <button onClick={(e) => { e.stopPropagation(); updateAdStatus(ad.id, aset.id, 'PAUSED'); }} disabled={updatingItem === ad.id}
                                              className="px-2.5 py-1 text-[10px] font-medium text-amber-600 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-md flex items-center gap-1 transition disabled:opacity-50"
                                              data-testid={`pause-ad-btn-${ad.id}`}>
                                              {updatingItem === ad.id ? <i className="fa-solid fa-spinner animate-spin"></i> : <i className="fa-solid fa-pause"></i>} Pause
                                            </button>
                                          )}
                                          <button onClick={(e) => { e.stopPropagation(); setShowPreview({ ad }); }}
                                            className="px-2 py-1 text-[10px] font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-md flex items-center gap-1 transition"
                                            data-testid={`preview-ad-btn-${ad.id}`}>
                                            <i className="fa-solid fa-eye"></i> Preview
                                          </button>
                                          <button onClick={(e) => { e.stopPropagation(); setShowEditAd({ ad, adsetId: aset.id }); }}
                                            className="px-2 py-1 text-[10px] font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-md flex items-center gap-1 transition"
                                            data-testid={`edit-ad-btn-${ad.id}`}>
                                            <i className="fa-solid fa-pen"></i> Edit
                                          </button>
                                          <button onClick={(e) => { e.stopPropagation(); setShowImageUpdate({ ad, adsetId: aset.id }); }}
                                            className="px-2 py-1 text-[10px] font-medium text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-md flex items-center gap-1 transition"
                                            data-testid={`change-image-btn-${ad.id}`}>
                                            <i className="fa-solid fa-image"></i> Image बदलें
                                          </button>
                                          {connectionStatus?.account_id && (
                                            <a href={metaAdsManagerUrl(connectionStatus.account_id, { campaignId: camp.id, adsetId: aset.id, adId: ad.id })}
                                              target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                                              className="px-2 py-1 text-[10px] font-medium text-[#1877F2] bg-[#e7f0fd] hover:bg-[#d4e4fb] border border-[#bcd5f5] rounded-md flex items-center gap-1 transition"
                                              data-testid={`meta-link-ad-${ad.id}`} title="यह ad Meta Ads Manager में खोलें">
                                              <i className="fa-brands fa-facebook"></i> Meta में देखें
                                            </a>
                                          )}
                                          <StatusBadge status={ad.status} />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Not Connected */}
      {!connectionStatus?.connected && (
        <div className="bg-white rounded-xl border p-8 text-center" data-testid="fb-not-connected">
          <i className="fa-brands fa-facebook text-5xl text-gray-300 mb-4"></i>
          <h3 className="font-bold text-gray-700 text-lg">Facebook Ads Connected नहीं है</h3>
          <p className="text-gray-500 text-sm mt-2 max-w-md mx-auto">
            अपने स्कूल का Facebook Ads जोड़ने के लिए <b>Settings → Integrations → Meta Connection</b> में जाएँ और Ad Account ID + System User Token भरकर "Test Connection" करें।
          </p>
          <a href="/settings" className="inline-block mt-4 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700" data-testid="fb-goto-settings">⚙️ Settings → Integrations खोलें</a>
        </div>
      )}

      {/* ===== MODALS ===== */}

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" data-testid="create-campaign-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg mx-4 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="bg-gradient-to-r from-[#1877F2] to-[#0d65d9] px-6 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3 text-white">
                <i className="fa-brands fa-facebook-f text-xl"></i>
                <h3 className="text-lg font-bold">New Facebook Campaign</h3>
              </div>
              <button onClick={() => setShowCreateModal(false)} className="text-white/70 hover:text-white transition" data-testid="close-create-modal">
                <i className="fa-solid fa-xmark text-xl"></i>
              </button>
            </div>
            <div className="p-6 space-y-5 overflow-y-auto">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Campaign Name *</label>
                <input type="text" value={newCampaign.name} onChange={e => setNewCampaign({ ...newCampaign, name: e.target.value })}
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#1877F2] focus:ring-0 outline-none transition"
                  placeholder="e.g., School Admission Campaign 2026" data-testid="campaign-name-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Campaign Objective</label>
                <div className="grid grid-cols-1 gap-2">
                  {OBJECTIVES.map(obj => (
                    <button key={obj.value} onClick={() => setNewCampaign({ ...newCampaign, objective: obj.value })}
                      className={`flex items-center gap-3 p-3 rounded-xl border-2 transition text-left ${newCampaign.objective === obj.value ? 'border-[#1877F2] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                      data-testid={`objective-${obj.value}`}>
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${newCampaign.objective === obj.value ? 'bg-[#1877F2] text-white' : 'bg-gray-100 text-gray-500'}`}>
                        <i className={`fa-solid ${obj.icon} text-sm`}></i>
                      </div>
                      <div>
                        <p className={`text-sm font-semibold ${newCampaign.objective === obj.value ? 'text-[#1877F2]' : 'text-gray-700'}`}>{obj.label}</p>
                        <p className="text-xs text-gray-400">{obj.desc}</p>
                      </div>
                      {newCampaign.objective === obj.value && <i className="fa-solid fa-circle-check text-[#1877F2] ml-auto"></i>}
                    </button>
                  ))}
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
                <p className="text-sm font-semibold text-blue-800 flex items-center gap-2">
                  <i className="fa-solid fa-circle-info"></i> Budget यहाँ नहीं — Ad Set में सेट होगा
                </p>
                <p className="text-xs text-blue-600 mt-1 leading-relaxed">
                  Meta में budget <b>Ad Set level</b> पर सेट होता है (audience + budget एक साथ)।
                  पहले Campaign (सिर्फ़ goal) बनाएँ, फिर उसके अंदर Ad Set बनाते समय Daily Budget डालें।
                  इसीलिए यहाँ budget नहीं माँगा जा रहा।
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Initial Status</label>
                <div className="flex gap-3">
                  {['PAUSED', 'ACTIVE'].map(st => (
                    <button key={st} onClick={() => setNewCampaign({ ...newCampaign, status: st })}
                      className={`flex-1 py-2.5 rounded-xl border-2 font-medium text-sm transition ${newCampaign.status === st ? (st === 'ACTIVE' ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-amber-400 bg-amber-50 text-amber-700') : 'border-gray-200 text-gray-500'}`}
                      data-testid={`status-option-${st}`}>
                      <i className={`fa-solid ${st === 'ACTIVE' ? 'fa-play' : 'fa-pause'} mr-2`}></i>
                      {st === 'ACTIVE' ? 'Active (तुरंत शुरू)' : 'Paused (बाद में शुरू)'}
                    </button>
                  ))}
                </div>
                {newCampaign.status === 'ACTIVE' && (
                  <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
                    <i className="fa-solid fa-triangle-exclamation"></i>
                    Active status में campaign तुरंत Meta पर live हो जाएगा
                  </p>
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowCreateModal(false)} className="flex-1 py-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 font-medium text-gray-600" data-testid="cancel-create-btn">Cancel</button>
                <button onClick={createCampaign} disabled={!newCampaign.name.trim() || creating}
                  className="flex-1 py-3 bg-[#1877F2] text-white rounded-xl hover:bg-[#1565c0] font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="submit-create-campaign-btn">
                  {creating ? <><i className="fa-solid fa-spinner animate-spin"></i> Creating...</> : <><i className="fa-solid fa-rocket"></i> Create Campaign</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ad Set Create Modal */}
      {showAdSetModal && (
        <AdSetCreateModal
          campaignId={showAdSetModal.campaignId}
          campaignName={showAdSetModal.campaignName}
          token={token}
          onClose={() => setShowAdSetModal(null)}
          onCreated={() => { loadAdSets(showAdSetModal.campaignId); }}
        />
      )}

      {/* 1-Click WhatsApp Lead Ad */}
      {showQuickWA && (
        <QuickWhatsAppModal
          token={token}
          onClose={() => setShowQuickWA(false)}
          onCreated={() => { setShowQuickWA(false); loadAll(); }}
        />
      )}

      {/* Ad Create Modal */}
      {showAdModal && (
        <AdCreateModal
          adsetId={showAdModal.adsetId}
          adsetName={showAdModal.adsetName}
          token={token}
          onClose={() => setShowAdModal(null)}
          onCreated={() => { loadAds(showAdModal.adsetId); }}
        />
      )}
      {showImageUpdate && (
        <AdImageUpdateModal
          ad={showImageUpdate.ad}
          token={token}
          onClose={() => setShowImageUpdate(null)}
          onUpdated={() => { /* image swap keeps the same ad row */ }}
        />
      )}
      {showEditCampaign && (
        <CampaignEditModal
          campaign={showEditCampaign}
          token={token}
          onClose={() => setShowEditCampaign(null)}
          onSaved={() => { setShowEditCampaign(null); loadAll(); }}
        />
      )}
      {showEditAdset && (
        <AdSetEditModal
          adset={showEditAdset.adset}
          token={token}
          onClose={() => setShowEditAdset(null)}
          onSaved={() => { setShowEditAdset(null); loadAdSets(showEditAdset.campaignId); loadAdSets(showEditAdset.campaignId); }}
        />
      )}
      {showEditAd && (
        <AdEditModal
          ad={showEditAd.ad}
          token={token}
          onClose={() => setShowEditAd(null)}
          onSaved={() => { setShowEditAd(null); loadAds(showEditAd.adsetId); loadAds(showEditAd.adsetId); }}
        />
      )}
      {showPreview && (
        <AdPreviewModal
          ad={showPreview.ad}
          token={token}
          onClose={() => setShowPreview(null)}
        />
      )}
    </div>
  );
}

// ==================== AD PREVIEW MODAL (real Facebook render) ====================
const PREVIEW_FORMATS = [
  { value: 'MOBILE_FEED_STANDARD', label: '📱 Mobile Feed' },
  { value: 'DESKTOP_FEED_STANDARD', label: '💻 Desktop Feed' },
  { value: 'INSTAGRAM_STANDARD', label: '📷 Instagram' },
  { value: 'FACEBOOK_STORY_MOBILE', label: '🎬 FB Story' },
];

function AdPreviewModal({ ad, token, onClose }) {
  const [format, setFormat] = useState('MOBILE_FEED_STANDARD');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true); setError(''); setBody('');
      try {
        const res = await fetch(`${API_URL}/api/campaign/facebook-ads/ad/${ad.id}/preview?ad_format=${format}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (cancelled) return;
        if (data.success && data.body) setBody(data.body);
        else setError(data.detail || 'Preview लोड नहीं हो पाया');
      } catch (e) {
        if (!cancelled) setError('Preview लोड नहीं हो पाया: ' + e.message);
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [ad.id, format, token]);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="ad-preview-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-4 rounded-t-2xl flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2"><i className="fa-solid fa-eye"></i> Ad Preview</h3>
            <p className="text-sm text-white/70 truncate">{ad.name}</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white text-xl" data-testid="ad-preview-close">✕</button>
        </div>
        <div className="p-4">
          <p className="text-xs text-gray-500 mb-2">Facebook पर असल में ऐसा दिखेगा 👇</p>
          <div className="flex gap-2 flex-wrap mb-4">
            {PREVIEW_FORMATS.map(f => (
              <button key={f.value} onClick={() => setFormat(f.value)}
                data-testid={`preview-format-${f.value}`}
                className={`text-xs px-3 py-1.5 rounded-full font-medium ${format === f.value ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {f.label}
              </button>
            ))}
          </div>
          {loading ? (
            <div className="py-12 text-center text-gray-400 text-sm"><i className="fa-solid fa-spinner animate-spin mr-2"></i>Preview लोड हो रहा है...</div>
          ) : error ? (
            <div className="py-8 text-center text-red-500 text-sm" data-testid="ad-preview-error">{error}</div>
          ) : (
            <div className="flex justify-center overflow-x-auto" data-testid="ad-preview-frame"
              dangerouslySetInnerHTML={{ __html: body }} />
          )}
          <p className="text-[11px] text-gray-400 mt-3 text-center">यह live preview सीधे Facebook से आता है।</p>
        </div>
      </div>
    </div>
  );
}

